import { useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Image, Trash2 } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { FormField } from '@/components/ui/form';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import {
  useInvitationGallery,
  useAddGalleryPhoto,
  useDeleteGalleryPhoto,
} from '@/hooks/useInvitations';
import { useAuth } from '@/hooks/useAuth';
import { InvitationFormValues } from '@/types/invitation-schema';
import AlbumModeration from './AlbumModeration';

interface Props {
  invitationId: string | undefined;
}

const MAX_FILE_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/heic'];
const VALID_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp', 'heic'];

const GalleryTab = ({ invitationId }: Props) => {
  const form = useFormContext<InvitationFormValues>();
  const { user } = useAuth();
  const isNew = !invitationId;
  const guestPhotosEnabled = form.watch('guest_photos_enabled');
  const { data: gallery } = useInvitationGallery(invitationId || '');
  const addGalleryPhoto = useAddGalleryPhoto();
  const deleteGalleryPhoto = useDeleteGalleryPhoto();
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const uploadFile = async (file: File): Promise<void> => {
    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error(`${file.name}: solo se permiten imágenes (JPG, PNG, WebP, HEIC)`);
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      toast.error(`${file.name}: la imagen no debe superar 5 MB`);
      return;
    }
    const fileExt = file.name.split('.').pop()?.toLowerCase();
    if (!fileExt || !VALID_EXTENSIONS.includes(fileExt)) {
      toast.error(`${file.name}: extensión no válida`);
      return;
    }

    const fileName = `${user!.id}/${invitationId}/${Date.now()}-${Math.random().toString(36).slice(2)}.${fileExt}`;
    const { error: uploadError } = await supabase.storage
      .from('invitation-photos')
      .upload(fileName, file);

    if (uploadError) {
      toast.error(`${file.name}: error al subir la foto`);
      return;
    }

    const { data: urlData } = supabase.storage
      .from('invitation-photos')
      .getPublicUrl(fileName);

    await addGalleryPhoto.mutateAsync({
      invitation_id: invitationId!,
      photo_url: urlData.publicUrl,
    });
  };

  const handleFiles = async (files: FileList | File[]) => {
    if (!invitationId || !user) return;
    const arr = Array.from(files);
    if (arr.length === 0) return;
    setUploading(true);
    await Promise.allSettled(arr.map(uploadFile));
    setUploading(false);
    if (inputRef.current) inputRef.current.value = '';
  };

  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) handleFiles(e.target.files);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => setIsDragOver(false);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    handleFiles(e.dataTransfer.files);
  };

  return (
    <div className="space-y-4">
    <Card>
      <CardHeader>
        <CardTitle>Galería de Fotos</CardTitle>
        <CardDescription>Sube fotos para mostrar en tu invitación</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <FormField
          control={form.control}
          name="couple_gallery_enabled"
          render={({ field }) => (
            <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
              <div>
                <p className="font-medium">Galería de pareja</p>
                <p className="text-sm text-muted-foreground">
                  Mostrar fotos que tú subas
                </p>
              </div>
              <Switch checked={field.value} onCheckedChange={field.onChange} />
            </div>
          )}
        />

        <FormField
          control={form.control}
          name="guest_photos_enabled"
          render={({ field }) => (
            <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
              <div>
                <p className="font-medium">Álbum de invitados</p>
                <p className="text-sm text-muted-foreground">
                  Permitir que los invitados suban fotos a un álbum compartido
                </p>
              </div>
              <Switch checked={field.value} onCheckedChange={field.onChange} />
            </div>
          )}
        />

        {guestPhotosEnabled && (
          <FormField
            control={form.control}
            name="guest_photos_auto_approve"
            render={({ field }) => (
              <div className="flex items-center justify-between p-4 bg-muted/60 rounded-lg ml-4">
                <div>
                  <p className="font-medium">Aprobar fotos automáticamente</p>
                  <p className="text-sm text-muted-foreground">
                    Si lo apagas, las fotos quedan pendientes hasta que las apruebes
                  </p>
                </div>
                <Switch checked={field.value} onCheckedChange={field.onChange} />
              </div>
            )}
          />
        )}

        {!isNew && (
          <>
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer ${
                isDragOver ? 'border-primary bg-primary/5' : 'border-muted-foreground/30'
              } ${uploading ? 'opacity-60 pointer-events-none' : ''}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => inputRef.current?.click()}
            >
              <input
                ref={inputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handlePhotoUpload}
                className="hidden"
              />
              <Image className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              {uploading ? (
                <p className="font-medium text-muted-foreground">Subiendo…</p>
              ) : (
                <>
                  <p className="font-medium">Arrastra fotos aquí o haz clic para seleccionar</p>
                  <p className="text-sm text-muted-foreground">
                    JPG, PNG, WebP, HEIC · hasta 5 MB · puedes seleccionar varias a la vez
                  </p>
                </>
              )}
            </div>

            {gallery && gallery.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                {gallery.map((photo) => (
                  <div key={photo.id} className="relative group">
                    <img
                      src={photo.photo_url}
                      alt={photo.caption || 'Foto de boda'}
                      className="w-full h-32 object-cover rounded-lg"
                    />
                    <Button
                      variant="destructive"
                      size="icon"
                      type="button"
                      className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteGalleryPhoto.mutate({ id: photo.id, invitationId });
                      }}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {isNew && (
          <p className="text-muted-foreground text-center py-4">
            Guarda la invitación primero para subir fotos
          </p>
        )}
      </CardContent>
    </Card>

    {/* 009 — Moderación del álbum de invitados (US2/US4) */}
    {!isNew && guestPhotosEnabled && invitationId && (
      <AlbumModeration invitationId={invitationId} />
    )}
    </div>
  );
};

export default GalleryTab;
